import { FC, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex } from '@chakra-ui/react';
import {
  Callout,
  Typography,
  View,
  XStack,
  YStack,
} from '@shinetools/sunshine-universal';
import {
  CardNumberElement,
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripeCardNumberElement, StripeElements } from '@stripe/stripe-js';
import { motion } from 'framer-motion';

import { PackageType } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import formatPrice from 'features/Onboarding/modules/PricingPlan/pages/PackageRecap/lib/formatPrice';
import {
  CardCvcInput,
  CardExpiryInput,
  CardNumberInput,
} from 'features/Stripe';
import stripePromise from 'features/Stripe/utils/stripe';

import { OnboardingSasuOffersDocument } from '../../graphql/queries/onboardingSasuOffers.gql';
import { OnboardingPackageItem } from '../../machine/model';
import locales from './locales';

interface PackagePaymentProps {
  pack: OnboardingPackageItem;
  isSubmittingPayment: boolean;
  onPay: (
    payload:
      | { cardNumberElement: StripeCardNumberElement }
      | { elements: StripeElements },
  ) => void;
  paymentSecret: string;
}

const PackagePayment: FC<PackagePaymentProps> = ({
  isSubmittingPayment,
  onPay,
  pack,
  paymentSecret,
}) => {
  const [acceptExpressCheckout, setAcceptExpressCheckout] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!stripe) {
      return;
    }

    (async () => {
      try {
        const { error, paymentIntent } =
          await stripe.retrievePaymentIntent(paymentSecret);

        if (error) {
          throw error;
        }

        setAmount(paymentIntent.amount);
      } catch (error) {
        setAmount(0);
      }
    })();
  }, [paymentSecret, stripe]);

  const handleCardPayment = async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error: formError } = await elements.submit();

    if (formError) {
      throw formError;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      throw new Error('Card element not found');
    }

    onPay({ cardNumberElement });
  };

  const handleExpressPayment = async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error: formError } = await elements.submit();

    if (formError) {
      throw formError;
    }

    onPay({ elements });
  };

  return (
    <form
      onSubmit={(event) => {
        event?.preventDefault();
        handleCardPayment();
      }}
    >
      <YStack gap="$space.24">
        <YStack gap="$space.16">
          <Box marginBottom="-4px">
            <CardNumberInput
              disabled={isSubmittingPayment}
              elementProps={{
                options: {
                  disableLink: true,
                  showIcon: true,
                },
              }}
            />
          </Box>

          <Flex flexDirection="row" marginBottom="-4px">
            <Box flex={1}>
              <CardExpiryInput disabled={isSubmittingPayment} />
            </Box>
            <Box flex={1} marginLeft="space-24" maxWidth="33%">
              <CardCvcInput disabled={isSubmittingPayment} />
            </Box>
          </Flex>
        </YStack>

        <Button isLoading={isSubmittingPayment} type="submit">
          {amount !== null
            ? locales.formatString(locales.payButtonWithAmount, {
                amount: formatPrice(amount),
              })
            : locales.payButton}
        </Button>

        <motion.div
          animate={{
            height: acceptExpressCheckout && !isSubmittingPayment ? 'auto' : 0,
          }}
          style={{ overflow: 'hidden' }}
        >
          <YStack gap="$space.24">
            <XStack alignItems="center" gap="$space.32">
              <View
                background="$color.grey.200"
                display="block"
                flex={1}
                height={2}
              />
              <Typography.Text bold textAlign="center" variant="discreet">
                {locales.or}
              </Typography.Text>

              <View
                background="$color.grey.200"
                display="block"
                flex={1}
                height={2}
              />
            </XStack>
            <ExpressCheckoutElement
              onClick={({ resolve }) => {
                resolve();
              }}
              onConfirm={() => handleExpressPayment()}
              onReady={(event) => {
                setAcceptExpressCheckout(
                  Boolean(event.availablePaymentMethods),
                );
              }}
            />
          </YStack>
        </motion.div>

        <Typography.Text size="small" textAlign="center">
          {locales.subCTAlabel}
        </Typography.Text>

        <Callout withIcon={false}>
          <Callout.Title>{locales.detailsTitle}</Callout.Title>
          <Callout.Content>
            {locales.formatString(locales.detailsBody, {
              planName: `Shine ${pack.plan.brandName}`,
              pricePerMonth: formatPrice(pack.plan.priceWithoutVAT),
              trialPeriod: pack.trialPeriod,
            })}
          </Callout.Content>
        </Callout>
      </YStack>
    </form>
  );
};

interface PackagePaymentContainerProps {
  providerPackageId: string;
  companyProfileId: string;
  onPrev: () => void;
  isSubmittingPayment: boolean;
  onPay: (
    payload:
      | { cardNumberElement: StripeCardNumberElement }
      | { elements: StripeElements },
  ) => void;
  paymentSecret: string;
}

const PackagePaymentContainer = asBentoPage<PackagePaymentContainerProps>(
  ({
    companyProfileId,
    Layout,
    onPrev,
    paymentSecret,
    providerPackageId,
    ...props
  }) => {
    const query = useQuery(OnboardingSasuOffersDocument, {
      variables: {
        companyProfileId,
        input: {
          type: PackageType.CapitalDeposit,
        },
      },
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header title={locales.title} />

        <SuspenseQuery query={query}>
          {({ viewer: { company } }) => {
            const pack = company.onboardingPackages.find(
              (p) => p.providerPackageId === providerPackageId,
            );

            if (!pack) {
              throw new Error('Package not found');
            }

            return (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
              >
                <Elements
                  options={{ clientSecret: paymentSecret }}
                  stripe={stripePromise}
                >
                  <PackagePayment
                    {...props}
                    pack={pack as OnboardingPackageItem}
                    paymentSecret={paymentSecret}
                  />
                </Elements>
              </motion.div>
            );
          }}
        </SuspenseQuery>
      </Layout>
    );
  },
);

export default PackagePaymentContainer;
